/**
 * 媒體庫上傳面板 —— 拖放區、上傳佇列、逐檔進度。
 *
 * 原本上傳邏輯直接寫在 MediaLibrary 裡，用 for 迴圈序列送且無進度回饋：
 * 20 個檔就是一個一個等，整段期間畫面只有「上傳中…」，用起來像壞掉。
 *
 * 併發是安全的 —— 伺服器端 `cms-media.service.ts` 的 upload 已對唯一索引衝突
 * （23505）收斂成「回既有列」，同內容並行上傳的結果與去重命中相同。舊註解說
 * 序列化是為了讓去重命中，那個顧慮後端早已處理掉了。
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from './Button';
import { Pill } from './Pill';
import { uploadMediaWithProgress, type MediaItem } from '../lib/api-client';
import {
  folderTagOf,
  formatBytes,
  languageFromPath,
  MAX_BATCH_FILES,
  pickedFromDataTransfer,
  pickedFromFileList,
  validateFile,
  type PickedFile,
} from '../lib/media-upload';

/**
 * 併發上限。
 *
 * 後端的 multipart 是記憶體暫存（FileInterceptor 預設），檔案整份常駐 heap；
 * 影片上限放寬到 200 MB 後，三支同時上傳就是 600 MB。故除了檔數上限，另外限制
 * 在途位元組總量。單檔超過總量上限時仍允許它獨自上傳，否則 200 MB 的影片會永遠
 * 排不進去——也正因如此，大影片實際上是一支一支傳，這是刻意的。
 */
const MAX_CONCURRENT = 3;
const MAX_INFLIGHT_BYTES = 64 * 1024 * 1024;

type ItemStatus = 'invalid' | 'queued' | 'uploading' | 'done' | 'duplicate' | 'failed' | 'canceled';

interface QueueItem {
  id: string;
  file: File;
  /** 含檔名的相對路徑；拖放單檔時等同檔名。 */
  path: string;
  /** 由路徑推得的資料夾標籤（無目錄時為 null）。 */
  folderTag: string | null;
  /** 由路徑推得的語系；批次語系有值時以批次為準。 */
  pathLanguage: string | null;
  status: ItemStatus;
  loaded: number;
  error?: string;
  media?: MediaItem;
}

interface MediaUploadPanelProps {
  language: string;
  tags: string[];
  /** 任一檔上傳成功（含去重命中）後呼叫，供頁面重新整理清單。 */
  onUploaded: () => void;
  /** 點「檢視」時開啟該素材的預覽。 */
  onPreview: (media: MediaItem) => void;
}

const ACTIVE_STATUSES: ReadonlySet<ItemStatus> = new Set(['queued', 'uploading']);

export function MediaUploadPanel({
  language,
  tags,
  onUploaded,
  onPreview,
}: MediaUploadPanelProps): JSX.Element {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [dragging, setDragging] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const fileInput = useRef<HTMLInputElement>(null);
  const dirInput = useRef<HTMLInputElement>(null);
  const uid = useRef(0);
  const controllers = useRef(new Map<string, AbortController>());
  /** StrictMode 下 effect 會跑兩次，靠這個確保同一項不會被啟動兩次。 */
  const started = useRef(new Set<string>());
  /** 上次回報的百分比，用來抑制每個 progress 事件都觸發 re-render。 */
  const lastPercent = useRef(new Map<string, number>());

  // 批次設定以 ref 讀取：上傳排程在 effect 裡進行，若把 language / tags 放進
  // 相依陣列，使用者上傳到一半改標籤就會重跑排程。
  const settings = useRef({ language, tags });
  settings.current = { language, tags };

  const patch = useCallback((id: string, next: Partial<QueueItem>): void => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...next } : it)));
  }, []);

  const enqueue = useCallback((picked: PickedFile[]): void => {
    if (picked.length === 0) return;
    const truncated = picked.length > MAX_BATCH_FILES;
    const slice = truncated ? picked.slice(0, MAX_BATCH_FILES) : picked;
    setNotice(
      truncated
        ? `一次最多 ${MAX_BATCH_FILES} 個檔案，其餘 ${picked.length - MAX_BATCH_FILES} 個未加入`
        : null,
    );

    setItems((prev) => [
      ...prev,
      ...slice.map((p): QueueItem => {
        const invalid = validateFile(p.file);
        return {
          id: `u${++uid.current}`,
          file: p.file,
          path: p.path,
          folderTag: folderTagOf(p.path),
          pathLanguage: languageFromPath(p.path),
          status: invalid ? 'invalid' : 'queued',
          loaded: 0,
          ...(invalid ? { error: invalid } : {}),
        };
      }),
    ]);
  }, []);

  const start = useCallback(
    (item: QueueItem): void => {
      const controller = new AbortController();
      controllers.current.set(item.id, controller);
      patch(item.id, { status: 'uploading', loaded: 0 });

      const { language: batchLanguage, tags: batchTags } = settings.current;
      const mergedTags = item.folderTag ? [...batchTags, item.folderTag] : batchTags;
      const effectiveLanguage = batchLanguage || item.pathLanguage || '';

      uploadMediaWithProgress(
        item.file,
        {
          ...(effectiveLanguage ? { language: effectiveLanguage } : {}),
          tags: mergedTags,
        },
        {
          signal: controller.signal,
          onProgress: (loaded) => {
            const pct = item.file.size > 0 ? Math.floor((loaded / item.file.size) * 100) : 0;
            if (lastPercent.current.get(item.id) === pct) return;
            lastPercent.current.set(item.id, pct);
            patch(item.id, { loaded });
          },
        },
      )
        .then((res) => {
          patch(item.id, {
            status: res.deduplicated ? 'duplicate' : 'done',
            loaded: item.file.size,
            media: res.media,
          });
          onUploaded();
        })
        .catch((e: unknown) => {
          if (e instanceof DOMException && e.name === 'AbortError') {
            patch(item.id, { status: 'canceled' });
            return;
          }
          patch(item.id, { status: 'failed', error: (e as Error).message });
        })
        .finally(() => {
          controllers.current.delete(item.id);
          lastPercent.current.delete(item.id);
        });
    },
    [patch, onUploaded],
  );

  // 排程：每次佇列變動就檢查是否有餘裕啟動下一個。effect 在 commit 後執行，
  // 此時 items 已是最新，不會出現用舊值判斷併發數的競態。
  useEffect(() => {
    const uploading = items.filter((it) => it.status === 'uploading');
    let count = uploading.length;
    let bytes = uploading.reduce((sum, it) => sum + it.file.size, 0);

    for (const item of items) {
      if (item.status !== 'queued' || started.current.has(item.id)) continue;
      if (count >= MAX_CONCURRENT) break;
      // count === 0 的例外：單檔大於總量上限時仍得讓它跑，否則永遠排不進去
      if (count > 0 && bytes + item.file.size > MAX_INFLIGHT_BYTES) continue;
      started.current.add(item.id);
      count += 1;
      bytes += item.file.size;
      start(item);
    }
  }, [items, start]);

  // 離開頁面時中止在途上傳，避免 XHR 回呼寫進已卸載的元件
  useEffect(() => {
    const inflight = controllers.current;
    return () => {
      for (const c of inflight.values()) c.abort();
    };
  }, []);

  const cancel = (id: string): void => {
    const c = controllers.current.get(id);
    if (c) {
      c.abort();
      return;
    }
    // 還沒開始的直接標成已取消，不必等排程
    started.current.add(id);
    patch(id, { status: 'canceled' });
  };

  const retry = (id: string): void => {
    started.current.delete(id);
    patch(id, { status: 'queued', loaded: 0, error: undefined });
  };

  const cancelAll = (): void => {
    for (const it of items) {
      if (ACTIVE_STATUSES.has(it.status)) cancel(it.id);
    }
  };

  const clearFinished = (): void => {
    setItems((prev) => prev.filter((it) => ACTIVE_STATUSES.has(it.status)));
  };

  const handleDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    setDragging(false);
    void pickedFromDataTransfer(e.dataTransfer).then(enqueue);
  };

  const active = items.filter((it) => ACTIVE_STATUSES.has(it.status)).length;
  const counts = {
    done: items.filter((it) => it.status === 'done').length,
    duplicate: items.filter((it) => it.status === 'duplicate').length,
    failed: items.filter((it) => it.status === 'failed' || it.status === 'invalid').length,
  };

  return (
    <div>
      <input
        ref={fileInput}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          if (e.target.files) enqueue(pickedFromFileList(e.target.files));
          e.target.value = '';
        }}
      />
      <input
        ref={dirInput}
        type="file"
        multiple
        // React 未內建這兩個屬性的型別；資料夾選取只能靠它們
        {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
        style={{ display: 'none' }}
        onChange={(e) => {
          if (e.target.files) enqueue(pickedFromFileList(e.target.files));
          e.target.value = '';
        }}
      />

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        style={{
          border: `2px dashed ${dragging ? '#2563eb' : '#d1d5db'}`,
          background: dragging ? '#eff6ff' : '#fafafa',
          borderRadius: 8,
          padding: 16,
          margin: '12px 0',
          transition: 'background 120ms, border-color 120ms',
        }}
      >
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button variant="primary" onClick={() => fileInput.current?.click()}>
            + 選擇檔案
          </Button>
          <Button variant="secondary" onClick={() => dirInput.current?.click()}>
            選擇資料夾
          </Button>
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            或把檔案／整個資料夾拖放到這裡。子目錄會自動成為標籤，路徑中的 zh / en / jp
            會帶出語系。上限：圖片 20 MB／語音 50 MB／影片 200 MB。
          </span>
        </div>
      </div>

      {notice && (
        <div className="error-banner" style={{ margin: '8px 0' }}>
          {notice}
        </div>
      )}

      {items.length > 0 && (
        <div className="table-card" style={{ margin: '12px 0' }}>
          <div
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              flexWrap: 'wrap',
              padding: '8px 12px',
              borderBottom: '1px solid #e5e7eb',
              fontSize: 13,
            }}
          >
            <strong>
              上傳佇列 {items.length} 筆{active > 0 ? `（進行中 ${active}）` : ''}
            </strong>
            <span style={{ color: '#6b7280' }}>
              成功 {counts.done}／已存在 {counts.duplicate}／失敗 {counts.failed}
            </span>
            <span style={{ flex: 1 }} />
            {active > 0 && (
              <Button variant="secondary" onClick={cancelAll}>
                全部取消
              </Button>
            )}
            <Button variant="ghost" onClick={clearFinished} disabled={items.length === active}>
              清除已完成
            </Button>
          </div>

          <table>
            <thead>
              <tr>
                <th>檔案</th>
                <th style={{ width: 90 }}>大小</th>
                <th style={{ width: 180 }}>進度</th>
                <th style={{ width: 200 }}>狀態</th>
                <th style={{ width: 110 }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <QueueRow
                  key={it.id}
                  item={it}
                  onCancel={() => cancel(it.id)}
                  onRetry={() => retry(it.id)}
                  onPreview={onPreview}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function QueueRow({
  item,
  onCancel,
  onRetry,
  onPreview,
}: {
  item: QueueItem;
  onCancel: () => void;
  onRetry: () => void;
  onPreview: (media: MediaItem) => void;
}): JSX.Element {
  const pct =
    item.file.size > 0 ? Math.min(100, Math.round((item.loaded / item.file.size) * 100)) : 0;

  return (
    <tr>
      <td style={{ fontSize: 13, maxWidth: 320, wordBreak: 'break-all' }}>
        {item.path}
        {(item.folderTag || item.pathLanguage) && (
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
            {item.folderTag && <>標籤 {item.folderTag}</>}
            {item.folderTag && item.pathLanguage && ' · '}
            {item.pathLanguage && <>語系 {item.pathLanguage}</>}
          </div>
        )}
      </td>
      <td style={{ fontSize: 12 }}>{formatBytes(item.file.size)}</td>
      <td>
        {item.status === 'uploading' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div
              style={{
                flex: 1,
                height: 6,
                background: '#e5e7eb',
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${pct}%`,
                  height: '100%',
                  background: '#2563eb',
                  transition: 'width 120ms linear',
                }}
              />
            </div>
            {/*
              進度 100% 只代表**瀏覽器送完了**，伺服器還要算 sha256、寫物件、
              INSERT。停在「100%」會讓人以為卡住，故此階段改顯示「處理中」。

              這段空窗也是取消語意的邊界：XHR.abort() 只能中止用戶端，已送達
              且處理完成的請求收不回來，此時取消再重試會命中去重。
            */}
            <span style={{ fontSize: 11, color: '#6b7280', minWidth: 44, textAlign: 'right' }}>
              {pct >= 100 ? '處理中' : `${pct}%`}
            </span>
          </div>
        ) : (
          <span style={{ fontSize: 12, color: '#9ca3af' }}>—</span>
        )}
      </td>
      <td style={{ fontSize: 12 }}>
        <StatusCell item={item} />
      </td>
      <td>
        <RowActions item={item} onCancel={onCancel} onRetry={onRetry} onPreview={onPreview} />
      </td>
    </tr>
  );
}

function StatusCell({ item }: { item: QueueItem }): JSX.Element {
  switch (item.status) {
    case 'queued':
      return <Pill>等待中</Pill>;
    case 'uploading':
      return <Pill tone="brand">上傳中</Pill>;
    case 'done':
      return <Pill tone="success">上傳成功</Pill>;
    // 去重是正常結果不是警告 —— 用 warning 黃色會被當成失敗而反覆重傳
    case 'duplicate':
      return <Pill>已存在（內容相同，未重複儲存）</Pill>;
    case 'canceled':
      return <Pill>已取消</Pill>;
    default:
      return <Pill tone="danger">{item.error ?? '失敗'}</Pill>;
  }
}

function RowActions({
  item,
  onCancel,
  onRetry,
  onPreview,
}: {
  item: QueueItem;
  onCancel: () => void;
  onRetry: () => void;
  onPreview: (media: MediaItem) => void;
}): JSX.Element | null {
  if (item.status === 'queued' || item.status === 'uploading') {
    return (
      <Button variant="secondary" onClick={onCancel}>
        取消
      </Button>
    );
  }
  if (item.status === 'failed' || item.status === 'canceled') {
    return (
      <Button variant="secondary" onClick={onRetry}>
        重試
      </Button>
    );
  }
  if (item.media) {
    const media = item.media;
    return (
      <Button variant="ghost" onClick={() => onPreview(media)}>
        檢視
      </Button>
    );
  }
  return null;
}
